/**
 * Llamadas a la API, compartidas por el panel y el portal.
 *
 * El token vive en `sessionStorage` y no en `localStorage`: una sesión abierta
 * en un ordenador compartido del muelle de recepción no debe seguir abierta
 * mañana porque nadie cerró la pestaña. Cada aplicación guarda el suyo con su
 * propia clave, así que entrar en el panel no cierra el portal ni al revés.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Qué se puede hacer a continuación. La API lo manda cuando lo hay. */
    readonly hint: string | null = null,
  ) {
    super(message);
  }
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
  /** Envío multipart (p. ej. albarán con PDF). */
  upload<T>(path: string, form: FormData): Promise<T>;
  /** Descarga como Blob (logos, ficheros) con la sesión puesta. */
  blob(path: string): Promise<Blob>;
  token(): string | null;
  setToken(token: string): void;
  clear(): void;
  /** Se llama cuando la API responde 401: la sesión ya no vale. */
  onUnauthorized(fn: () => void): void;
}

export function createApi(app: 'admin' | 'portal'): ApiClient {
  const key = `provalia.${app}.token`;
  let unauthorized: () => void = () => {};

  const token = (): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  async function request<T>(path: string, init: RequestInit = {}, json = true): Promise<T> {
    const t = token();
    const headers: Record<string, string> = { ...(init.headers as Record<string, string> | undefined) };
    if (json && init.body !== undefined) headers['content-type'] = 'application/json';
    if (t) headers.authorization = `Bearer ${t}`;

    let res: Response;
    try {
      res = await fetch(`/api${path}`, { ...init, headers });
    } catch {
      throw new ApiError(0, 'No hay conexión con el servidor', 'Comprueba tu conexión a internet y vuelve a intentarlo');
    }
    const text = await res.text();
    let body: Record<string, unknown> = {};
    try {
      body = text === '' ? {} : (JSON.parse(text) as Record<string, unknown>);
    } catch {
      body = { error: text.slice(0, 300) };
    }
    if (!res.ok) {
      if (res.status === 401 && t) {
        clear();
        unauthorized();
      }
      throw new ApiError(
        res.status,
        typeof body.error === 'string' ? body.error : `Error ${res.status}`,
        typeof body.hint === 'string' ? body.hint : typeof body.estado === 'string' ? body.estado : null,
      );
    }
    return body as T;
  }

  function clear(): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* nada que hacer */
    }
  }

  return {
    get: (path) => request(path),
    post: (path, body) => request(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
    put: (path, body) => request(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
    del: (path) => request(path, { method: 'DELETE' }),
    upload: (path, form) => request(path, { method: 'POST', body: form }, false),
    async blob(path) {
      const t = token();
      const res = await fetch(`/api${path}`, { headers: t ? { authorization: `Bearer ${t}` } : {} });
      if (!res.ok) throw new ApiError(res.status, `Error ${res.status}`);
      return res.blob();
    },
    token,
    setToken(value) {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        /* la sesión durará lo que dure la página */
      }
    },
    clear,
    onUnauthorized(fn) {
      unauthorized = fn;
    },
  };
}

/** Lee un fichero de imagen como data URL, para subir logos. */
export function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('No se pudo leer el fichero'));
    reader.readAsDataURL(file);
  });
}
